import './PopupSelectOption.scss';

import Button from '@components/Button';
import ButtonTextX from '@components/ButtonTextX';
import ProductSwatch from '@components/ProductSwatch';
import useTranslation from '@hooks/useTranslation';
import type { AddToCartState } from '@providers/CartProvider';
import type { Dict } from '@types';
import { createClsNameMap, isMobileWidth, mapModifiers } from '@utils';
import classnames from '@utils/classnames';
import { isEqual, keyBy, keys, mapValues, update } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ProductDataOption, ProductDataVariant } from '@/widgets/QuickView';

export const clsNameMap = createClsNameMap({
  elements: {
    container: createClsNameMap(),
    backdrop: createClsNameMap(),
  },
  modifiers: ['fixed-bottom'],
})('popup-select-option');

export type PopupSelectOptionProps = {
  variants: Array<ProductDataVariant>;
  optionsWithValue: Array<ProductDataOption>;
  handleClosePopupSelectOption: () => void;
  isSoldOut?: boolean;
};
const PopupSelectOption = ({
  optionsWithValue,
  variants,
  handleClosePopupSelectOption,
  isSoldOut,
}: PopupSelectOptionProps) => {
  const { t } = useTranslation();

  const [addToCartState, setAddToCartState] = useState<AddToCartState>({
    isAdding: false,
    success: false,
    fail: false,
  });

  const [selectedVariant, setSelectedVariant] = useState<ProductDataVariant | undefined>(
    variants?.[0]
  );
  const isSoldOutVariantSelected = !selectedVariant?.available;

  const selectedVariantOptionsMapToObject: Dict = useMemo(() => {
    return mapValues(
      keyBy(
        selectedVariant?.original_merged_options || selectedVariant?.merged_options,
        (item) => item.split(':')[0]
      ),
      (value) => value.split(':')[1]
    );
  }, [selectedVariant]);

  const handleChangeVariant = (option: string, value: string) => {
    const newVariantOption = update({ ...selectedVariantOptionsMapToObject }, option, () => value);

    const newVariantOptionMapToArray = keys(newVariantOption).map(
      (key) => `${key}:${newVariantOption[key]}`
    );

    const newVariant = variants?.find((item) =>
      isEqual(item.original_merged_options || item.merged_options, newVariantOptionMapToArray)
    );

    setSelectedVariant(
      newVariant || {
        available: false,
        id: '',
        price: undefined,
        merged_options: newVariantOptionMapToArray,
      }
    );
  };

  const popupSelectOptionContainerRef = useRef(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (!popupSelectOptionContainerRef.current) return;

    if (!(popupSelectOptionContainerRef.current as HTMLElement).contains(e.target as Node)) {
      e.preventDefault();
      handleClosePopupSelectOption();
    } else if (!(e.target instanceof HTMLLabelElement)) {
      e.preventDefault();
    }
  };

  const handleClosePopup = () => {
    setTimeout(() => {
      handleClosePopupSelectOption();
      setAddToCartState((prev) => ({ fail: false, isAdding: false, success: false }));
    }, 350);
  };

  const handleATC = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (addToCartState?.isAdding) return;
    if (selectedVariant) {
      setAddToCartState((prev) => ({ ...prev, isAdding: true }));

      window.dispatchEvent(
        new CustomEvent('boost-sd-add-to-cart', {
          detail: {
            variantId: selectedVariant.id,
            quantity: 1,
            onSuccess: () => {
              handleClosePopup();
              setAddToCartState((prev) => ({ ...prev, isAdding: false, success: true }));
            },
            onFail: () => {
              handleClosePopup();
              setAddToCartState((prev) => ({ ...prev, fail: true, isAdding: false }));
            },
          },
        })
      );
    }
  };

  useEffect(() => {
    if (isMobileWidth()) document.body.style.overflow = 'hidden';
    window.addEventListener('click', handleClickOutside, true);

    return () => {
      if (isMobileWidth()) document.body.style.overflow = 'unset';
      window.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const buttonText = !isSoldOutVariantSelected
    ? addToCartState?.isAdding
      ? t('productItem.atcAddingToCartBtnLabel')
      : addToCartState?.success
      ? t('productItem.atcAddedToCartBtnLabel')
      : addToCartState?.fail
      ? t('productItem.atcFailedToCartBtnLabel')
      : t('productItem.atcAvailableLabel')
    : t('productItem.soldoutLabel');

  return (
    <>
      {isMobileWidth() && <div className={clsNameMap.elm('backdrop')}></div>}
      <div
        ref={popupSelectOptionContainerRef}
        className={mapModifiers(clsNameMap, { 'fixed-bottom': isMobileWidth() })}
      >
        <div className={clsNameMap.elm('container')}>
          {optionsWithValue.map((option, index: number) => (
            <ProductSwatch<string>
              options={option.values.map((value) => ({
                value: value.origin_title || value.title,
                label: value.title,
                id: `product-swatch-qv-${value.title}`,
                labelAs:
                  (option.original_name?.toLowerCase() || option.name) === 'color'
                    ? 'image'
                    : 'text',
                displayType:
                  (option.original_name?.toLowerCase() || option.name) === 'size'
                    ? 'box'
                    : 'circle',
                showOptionValueOnHovering:
                  (option.original_name?.toLowerCase() || option.name) === 'color',
              }))}
              key={index}
              onSelect={(value) =>
                handleChangeVariant(option.original_name?.toLowerCase() || option.name, value)
              }
              label={option.label}
              selectOptionOnLabel
              defaultSelectedValue={option.values[0].title}
              isPopupSelectOption
            />
          ))}
          <ButtonTextX
            value='close-popup'
            onClick={handleClosePopupSelectOption}
            modifiers={['as-close-mini-popup']}
          />
        </div>
        <Button
          onClick={handleATC}
          disabled={isSoldOutVariantSelected}
          buttonModifiers={['primary', 'full-width', 'large']}
          className={
            buttonText === t('productItem.atcAvailableLabel') ? classnames('btn-add-to-cart') : ''
          }
        >
          {buttonText}
        </Button>
      </div>
    </>
  );
};

export default PopupSelectOption;
